/**
 * Centralized Analytics Wrapper
 *
 * This wrapper provides a single interface for all analytics tracking in DramaDetector.
 *
 * Key Benefits:
 * - Auto-injects experiment properties (exp_*, feature_flag_*) into every event
 * - Provides type-safe tracking via trackEvent() from events.ts
 * - Single source of truth for experiment context
 * - Makes migrations easier (change PostHog to Mixpanel? Update once here)
 *
 * Usage:
 *   import { analytics } from '@/lib/analytics-wrapper';
 *   analytics.capture('Button_Click_Login', { method: 'google' });
 *
 * WARNING: Never call posthog.capture() directly - always use this wrapper!
 *
 * Architecture Note (2025-12-16):
 * Uses window.posthog (script loader) instead of posthog-js npm package.
 * This avoids duplicate PostHog integrations - analytics.web.ts initializes
 * PostHog via script loader, and this wrapper piggybacks on that.
 *
 * Related: See TRACKING.md for complete analytics guide
 */

import { scrubObject } from '../apps/mobile/lib/analytics/scrubbers';

// TypeScript interface for PostHog on window
// Note: declared as 'any' to avoid conflicts with analytics.web.ts declaration
declare global {
  interface Window {
    posthog?: any;
  }
}

/**
 * Experiment context injected into all events
 *
 * These properties are automatically added to every analytics event.
 * Update this interface when adding new experiments or feature flags.
 */
interface ExperimentContext {
  // Active Experiments (A/B tests)
  exp_ai_model?: 'llama-3.3-70b' | 'gpt-4o' | 'claude-sonnet-4';
  exp_results_layout?: 'card_based' | 'list_view';
  exp_onboarding_flow?: 'progressive_trial' | 'email_gate';

  // Feature Flags (Boolean toggles)
  feature_flag_google_signin?: boolean;
  feature_flag_email_capture?: boolean;
  feature_flag_gamification?: boolean;

  // Add new experiments/flags here as they are created
}

/**
 * AnalyticsWrapper class
 *
 * Wraps PostHog SDK and auto-injects experiment context into all events.
 */
class AnalyticsWrapper {
  private experimentCache: ExperimentContext = {};
  private initialized = false;

  /**
   * Initialize the wrapper and fetch experiment variants
   *
   * Auto-initializes when window.posthog is ready (no manual call needed).
   * Can also be called manually to force refresh of experiment context.
   *
   * Usage:
   *   // Usually automatic, but can force refresh:
   *   await analytics.initialize();
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return; // Already initialized, skip silently
    }

    // Check if PostHog is available (script loader must have run)
    if (typeof window === 'undefined' || !window.posthog) {
      console.warn('[Analytics] PostHog not available yet');
      return;
    }

    try {
      // Wait for PostHog to load feature flags
      await window.posthog.reloadFeatureFlags();

      // Cache experiment variants
      this.experimentCache = {
        // Fetch active experiments
        exp_ai_model: (window.posthog.getFeatureFlag('ai_model_experiment') as
          | 'llama-3.3-70b'
          | 'gpt-4o'
          | 'claude-sonnet-4'
          | undefined),
        exp_results_layout: (window.posthog.getFeatureFlag('results_layout_experiment') as
          | 'card_based'
          | 'list_view'
          | undefined),
        exp_onboarding_flow: (window.posthog.getFeatureFlag('onboarding_flow_experiment') as
          | 'progressive_trial'
          | 'email_gate'
          | undefined),

        // Fetch feature flags
        feature_flag_google_signin: window.posthog.isFeatureEnabled('google_signin') || false,
        feature_flag_email_capture: window.posthog.isFeatureEnabled('email_capture') || false,
        feature_flag_gamification: window.posthog.isFeatureEnabled('gamification') || false,

        // Add new experiments/flags here as they are created
      };

      this.initialized = true;
      console.log('[Analytics] Wrapper initialized with experiment context');
    } catch (error) {
      console.error('[Analytics] Failed to initialize:', error);
      // Continue without experiment context rather than blocking app
      this.initialized = true;
    }
  }

  /**
   * Capture an analytics event with auto-injected experiment properties
   *
   * Usage:
   *   analytics.capture('Button_Click_Login', { method: 'google' });
   *
   * PostHog receives:
   *   {
   *     event: 'Button_Click_Login',
   *     properties: {
   *       method: 'google',
   *       exp_ai_model: 'llama-3.3-70b',    // Auto-injected
   *       feature_flag_google_signin: true, // Auto-injected
   *       ...
   *     }
   *   }
   */
  capture<TProperties extends Record<string, any>>(
    eventName: string,
    properties?: TProperties
  ): void {
    // Check if PostHog is available
    if (typeof window === 'undefined' || !window.posthog) {
      // SSR or PostHog not loaded - silently skip
      return;
    }

    // Auto-initialize on first capture (lazy initialization)
    if (!this.initialized) {
      this.initialize().catch(() => {
        // Initialization failed, but we still send the event
      });
    }

    // M4: Scrub sensitive keys before sending to PostHog (defense-in-depth)
    // Uses shared scrubObject for consistency with captureEvent, Sentry, and edge scrubbing
    const scrubbedProperties = properties ? scrubObject(properties) : undefined;

    // Inject PostHog session ID for session replay linking (PR #438)
    const sessionId =
      typeof window.posthog.get_session_id === 'function'
        ? window.posthog.get_session_id()
        : undefined;

    // Merge user properties with experiment context
    const enrichedProperties = {
      ...(sessionId && { $session_id: sessionId }),
      ...scrubbedProperties,
      ...this.experimentCache,
      timestamp: new Date().toISOString(),
    };

    // Send to PostHog via window.posthog (script loader)
    window.posthog.capture(eventName, enrichedProperties);

    // Log in development (helpful for debugging)
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Analytics] ${eventName}`, enrichedProperties);
    }
  }

  /**
   * Get the variant for a specific experiment
   *
   * Usage:
   *   const aiModel = analytics.getExperimentVariant('exp_ai_model');
   *   if (aiModel === 'llama-3.3-70b') {
   *     // Show Llama-specific UI
   *   }
   *
   * Returns:
   *   The variant string, or undefined if experiment not active
   */
  getExperimentVariant<K extends keyof ExperimentContext>(
    experimentKey: K
  ): ExperimentContext[K] {
    return this.experimentCache[experimentKey];
  }

  /**
   * Check if a feature flag is enabled
   *
   * Usage:
   *   if (analytics.isFeatureEnabled('google_signin')) {
   *     return <GoogleSignInButton />;
   *   }
   *
   * Returns:
   *   Boolean (defaults to false if flag not found)
   */
  isFeatureEnabled(flagKey: string): boolean {
    const key = `feature_flag_${flagKey}` as keyof ExperimentContext;
    return this.experimentCache[key] === true;
  }

  /**
   * Refresh experiment context (use if flags change mid-session)
   *
   * Normally not needed - experiments are fetched on app startup.
   * Use this if you dynamically update feature flags via PostHog API.
   */
  async refreshExperiments(): Promise<void> {
    this.initialized = false;
    await this.initialize();
  }

  /**
   * Identify a user (call after signup/login)
   *
   * Usage:
   *   analytics.identify('user_123', {
   *     email: 'user@example.com', // WARNING: Obfuscate PII in production!
   *     signup_date: '2025-01-04',
   *   });
   *
   * IMPORTANT: Never send raw PII (email, name, phone) in production.
   * Use hashed values or user IDs only for GDPR compliance.
   */
  identify(userId: string, properties?: Record<string, any>): void {
    if (typeof window === 'undefined' || !window.posthog) {
      return;
    }

    window.posthog.identify(userId, properties);

    if (process.env.NODE_ENV === 'development') {
      console.log(`[Analytics] Identified user: ${userId}`, properties);
    }
  }

  /**
   * Reset user identity (call on logout)
   *
   * Usage:
   *   analytics.reset();
   */
  reset(): void {
    if (typeof window === 'undefined' || !window.posthog) {
      return;
    }

    window.posthog.reset();

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Reset user identity');
    }
  }

  /**
   * Get all experiment context (for debugging)
   *
   * Usage:
   *   console.log('Current experiments:', analytics.getExperimentContext());
   */
  getExperimentContext(): ExperimentContext {
    return { ...this.experimentCache };
  }
}

/**
 * Singleton instance (use this everywhere)
 *
 * Usage:
 *   import { analytics } from '@/lib/analytics-wrapper';
 *   analytics.capture('Button_Click_Login', { method: 'google' });
 */
export const analytics = new AnalyticsWrapper();

/**
 * Export type for external usage
 */
export type { ExperimentContext };
